import { useEffect, useRef, useState } from 'react';

/**
 * 講義中に画面が消えないようにする。
 *
 * 非対応でもアプリは止めない。使えない旨を伝えて、端末側の設定で対処してもらう。
 * (機能検出に失敗しただけで翻訳が使えないのは本末転倒)
 */
export function useWakeLock(active: boolean): { supported: boolean; held: boolean } {
  const [held, setHeld] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!supported || !active) return;

    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        const sentinel = await navigator.wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        setHeld(true);
        sentinel.addEventListener('release', () => setHeld(false));
      } catch {
        // 電池残量が少ない等で拒否されることがある。翻訳は続行する。
        setHeld(false);
      }
    };

    // 画面を戻したときに解除されたままにならないよう、復帰時に取り直す。
    const handleVisibility = (): void => {
      if (document.visibilityState === 'visible' && !sentinelRef.current) void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      void sentinelRef.current?.release();
      sentinelRef.current = null;
      setHeld(false);
    };
  }, [active, supported]);

  return { supported, held };
}
